import { RenderCacheData } from "../ui/renderCacheData.types";
import { anyReallyAny, invert } from "./object.tools";

/**
 * Get the string given in parameter in titlecase format
 * @description 
 * helloworld => Helloworld
 * im a TeaPot => Im a TeaPot 
 */
export function capitalizeFirstLetter(str: string): string {
    if (str == null) { return str; }
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Get the string given in parameter in lowercase format
 * @description 
 * Helloworld => helloworld
 * Im a TeaPot => im a TeaPot 
 */
export function lowercaseFirstLetter(str: string): string {
    if (str == null) { return str; }
    return str.charAt(0).toLowerCase() + str.slice(1);
}


/**
 * return the string with all occurences of toReplace replaced by replaceBy
 */
export function replaceAll(phrase: string, toReplace: string, replaceBy: string): string {
    if (phrase == null || toReplace == null) { return phrase; }
    const str = phrase.replace(new RegExp(toReplace, "g"), replaceBy);
    return str;
}

/**
 * @param toTest string to test
 * @param criterias array of string or regexp to test
 * @returns true if toTest matches at least one criteria
 */
export function matchesExpressions(toTest: string, criterias: (string | RegExp)[]): boolean {
    if (criterias) {
        for (const i of criterias) {
            if (i instanceof RegExp) {
                if (i.test(toTest)) {
                    // file matches ignored regexp
                    return true;
                }
            } else if (i === toTest) {
                // file matches ignored folder name
                return true;
            }
        }
    }
    return false;
}

/**
 * @returns the longest shared portion for all strings in array
 * getSharedStart("shared_first","shared_second") => "shared_"
 */
export function getSharedStart(...strings: string[]): string {
    const sortedArray = strings.concat().sort();
    const firstString = sortedArray[0];
    if (!firstString) { return ""; }
    const firstStringLength = firstString?.length ?? 0;
    const lastString = sortedArray[sortedArray.length - 1];

    let i = 0;
    // since strings are sorted, for each char if first string equals the last, they all equal
    while (i < firstStringLength && firstString.charAt(i) === lastString.charAt(i)) { i++; }
    return firstString.substring(0, i);
}

/**
 * Remove html tags in a string
 *
 * @param {string} html
 * @returns {*}  {string}
 */
export function removeHtmlTags(html: string): string {
    return html.replace(/<[^>]+>/g, "");
}

/** Special character and its associated html entity */
export type EscapeMap = { [code: string]: string }

/** Internal list of HTML entities for escaping */
export const escapeMap: EscapeMap = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#x27;",
    "`": "&#x60;"
};

/** Internal list of HTML entities for unescaping */
export const unescapeMap = invert(escapeMap);

/**
 * Generate functions for escaping and unescaping strings to/from HTML interpolation
 *
 * @param map Map containing search value and replacer value
 * @returns {*} {(string: string) => string}
 */
export function createEscaper(map: EscapeMap): (string: string) => string {
    const escaper = function (match: string): string {
        return map[match];
    };

    // Regexes for identifying a key that needs to be escaped.
    const source = "(?:" + Object.keys(map).join("|") + ")";
    const testRegexp = RegExp(source);
    const replaceRegexp = RegExp(source, "g");

    return function (text: string): string {
        text = text == null ? "" : "" + text;
        return testRegexp.test(text) ? text.replace(replaceRegexp, escaper) : text;
    };
}

/**
 * Encode character into html entity
 *
 * @param html Html to encode
 * @returns {*}  {string}
 */
export function escapeHtml(html: string): string {
    const escaper = createEscaper(escapeMap);
    return escaper(html);
}

/**
 * Decode html entities into their original characters
 *
 * @param html Html to decode
 * @returns {*}  {string}
 */
export function unescapeHtml(html: string): string {
    const unescaper = createEscaper(unescapeMap);
    return unescaper(html);
}

export interface FilterOptionsParams<ItemData> {

    /** Full item to use for more complex filters */
    item: ItemData | null;

    /** Result of the smartTable's column GetCellValue. Used in default smartTable's filter comparator */
    itemValue: anyReallyAny;

    /** Input value from user */
    filterValue: string;
}

export interface FilterOptions<ItemData> {

    /** Comparator used to filter on column */
    filterComparator?: (p: FilterOptionsParams<ItemData>, renderCacheData?: RenderCacheData) => boolean;

    /** If true, there won't be any filter in the column header  */
    filterDisabled?: boolean;

    /** The default filter function will use these options */
    defaultFilterOptions?: FilterStringOptions;
}

/** Specify regular expression mode */
export enum FilterRegexMode {
    /** Only use JavaScript regex */
    RAW = 1,
    /** 
     * Only simple regex mode
     * Allow the user to specify a regex in a simple way, the entered filter will be converted to a JavaScript regex.
     */
    SIMPLE,
    /**
     * Allow both simple and raw regex.
     * Raw regex must be entered between the delimiters.
     */
    BOTH
}

/** Default character used as a Raw Regex delimiter in BOTH mode */
export const REGEX_FILTER_DEFAULT_DELIMITER = "/";
/** Default character for single character wildcard in regex filters */
export const REGEX_FILTER_DEFAULT_SINGLE_WILDCARD = "?";
/** Default character for multiple characters wildcard in regex filters */
export const REGEX_FILTER_DEFAULT_MULTI_WILDCARD = "*";

export interface FilterStringOptions {
    /** Use case sensitive filter, default false */
    caseSensitive?: boolean;

    /** Split used for multi search */
    split?: {
        /** Disable multi search */
        disable?: boolean,
        /** Use a custom splitter, default is "," */
        customSeparator?: string
    };

    /** Use "startsWith" to process filter, default is "includes" */
    startsWith?: boolean;

    regularExpression?: {
        /** Enable regular expression */
        enable: FilterRegexMode,
        /** 
         * Specify raw regular expression delimiter.
         * By default, REGEX_FILTER_DEFAULT_DELIMITER will be used.
         * 
         * Only used when BOTH mode is enabled.
         */
        delimiter?: string,
        /**
         * Single character wildcard.
         * By default, REGEX_FILTER_DEFAULT_SINGLE_WILDCARD will be used.
         * 
         * Only used when simple mode is available.
         */
        singleWildcard?: string,
        /**
         * None, one or multiple character wildcard.
         * By default, REGEX_FILTER_DEFAULT_MULTI_WILDCARD will be used.
         * Note: '\{multiWildcard}' will be replaced by the {multiWildcard} character.
         * 
         * Only used when simple mode is available.
         */
        multiWildcard?: string
    };
}

/** Escape regex characters if needed */
function _escapeRegexChar(c: string): string {
    switch (c) {
        case "/": // delimiter
        case "{":
        case "}":
        case "[":
        case "]":
        case "(":
        case ")":
        case "?":
        case "|":
        case ".":
        case "*":
        case "+":
        case "^":
        case "$":
            // Escape special characters that may be input by the user so the string can be processed
            // as a regex. Note that any special character used as wildcard or delimiter will be 
            // handled first so it won't be escaped and copied.
            return "\\" + c;
        default:
            return c;
    }
}

/** Build a standard regex from filter options */
function _buildFilterRegex(filterStr: string, options: FilterStringOptions): RegExp | null {
    // -- Init flags --
    const flags: string = options.caseSensitive ? "m" : "mi";

    // -- Init pattern --
    let pattern: string = filterStr;
    let mode: FilterRegexMode = options?.regularExpression?.enable ?? FilterRegexMode.RAW; // Regular expression should never be null here

    // -- Detect raw mode in BOTH mode --
    if (mode === FilterRegexMode.BOTH) {
        const delimiter = options.regularExpression?.delimiter ?? REGEX_FILTER_DEFAULT_DELIMITER;
        if (pattern.startsWith(delimiter)) {
            // A raw regex was input
            mode = FilterRegexMode.RAW;
            if (!pattern.endsWith(delimiter)) {
                // Cannot be filtered yet
                return null;
            }
            pattern = filterStr.substring(delimiter.length, filterStr.length - delimiter.length);
        } else {
            mode = FilterRegexMode.SIMPLE;
            // Pattern will be filtered later
        }
    }

    // -- Convert simple regex to a raw pattern --
    if (mode === FilterRegexMode.SIMPLE) {
        // Convert the filter string to a regex pattern
        const singleWildcard = options.regularExpression?.singleWildcard ?? REGEX_FILTER_DEFAULT_SINGLE_WILDCARD;
        const multiWildcard = options.regularExpression?.multiWildcard ?? REGEX_FILTER_DEFAULT_MULTI_WILDCARD;
        pattern = "";
        let carbonCopyNext: boolean = false;
        for (const c of filterStr) {
            if (carbonCopyNext) {
                carbonCopyNext = false;
                pattern += _escapeRegexChar(c);
                continue;
            }

            switch (c) {
                case "\\":
                    // Using a backslash before a char will copy it as-is
                    carbonCopyNext = true;
                    break;
                case singleWildcard:
                    // Convert single wildcard to single char
                    pattern += ".";
                    break;
                case multiWildcard:
                    // Convert multi wildcard to none, one or multiple chars
                    pattern += ".*";
                    break;
                default:
                    pattern += _escapeRegexChar(c);
                    break;
            }
        }
        // Force begin/end markers. It's user responsibility to add multiWildcard to produce an include
        pattern = "^" + pattern + "$";
    }
    try {
        return new RegExp(pattern, flags);
    } catch (e) {
        // It is possible that regular expression is invalid, mostly because the user
        // is still entering the regex, just refuse the regex
        return null;
    }
}

/**
 * @returns true if mainStr matches filterStr as a regex
 * @returns true if filterStr is an invalid regex
 */
function _matchRegex(mainStr: string, filterStr: string, options: FilterStringOptions): boolean {
    if (filterStr === "!") {
        // Empty cells
        return mainStr == null || mainStr === "";
    } else if (filterStr === "!!") {
        // Cell with some content
        return mainStr != null && mainStr !== "";
    } else {
        // Build regex and test pattern
        let invert: boolean = false;
        if (filterStr.startsWith("!")) {
            invert = true;
            filterStr = filterStr.substring(1);
        }
        const regex = _buildFilterRegex(filterStr, options); // Tested above, can be cast safely
        if (regex === null) {
            // If regular expression is invalid, return true so no filtering is applied.
            // This is simpler for the user to detect that filter is not applied.
            return true;
        }
        return invert !== regex.test(mainStr); // "!==" is a trick to invert the result if needed
    }
}

/**
 * Filter function
 * @param mainStr Main string to search in
 * @param filterStr String to search for
 * @param options 
 * @param options.caseSensitive If true, it does not lowercase both input strings
 * @param options.split 
 * @param options.split.disable If true, it does not split the filter string by comma, it will search for the complete filter string in the main string
 * @param options.split.customSeparator Split by custom separator rather than comma
 * @param options.startsWith If true, uses startsWith rather than includes
 * @param options.regularExpression Regular expression mode parameters. @see FilterStringOptions for more details
 * @returns Boolean indicating if there is a match
 */
export function filterString(mainStr: string, filterStr: string, options?: FilterStringOptions): boolean {
    const filterStrArray = options?.split?.disable ? [filterStr] : filterStr.split(options?.split?.customSeparator ?? ",");
    if (options?.regularExpression != null) {
        // -- Regular expression mode --
        for (const filterSplit of filterStrArray) {
            if (_matchRegex(mainStr, filterSplit, options)) {
                return true;
            }
        }
        return false;
    } else {
        // Only tested here because regular expressions have a special behavior due to ! and !!
        if (!mainStr || !filterStr) { return false; }

        // -- String filtering mode --
        const formattedMainStr = options?.caseSensitive ? mainStr : mainStr.toLowerCase(); // To lower case if needed

        for (const filterValue of filterStrArray) {
            if (!filterValue) { continue; }
            const formattedFilterValue = options?.caseSensitive ? filterValue : filterValue.toLowerCase().trim();
            const searchInStr = formattedFilterValue.normalize("NFD").match(/\p{Diacritic}/gu) ? formattedMainStr : formattedMainStr.normalize("NFD").replace(/\p{Diacritic}/gu, ""); // remove accents (cf. https://stackoverflow.com/a/37511463/5763167)
            if (options?.startsWith ? searchInStr.startsWith(formattedFilterValue) : searchInStr.includes(formattedFilterValue)) { return true; }
        }
        return false;
    }
}

/** 
 * Remove duplicate white spaces in a text, replacing them by a single one 
 * 
 * White space is understood as spaces and tabs only so that \n will be kept.
 */
export function removeDuplicateWhiteSpaces(str: string): string {
    return str.replace(/[\t ]+/g, " ");
}

/**
 * Returns a (basic) hash code from a string
 * @param  {string} str The string to hash.
 * @param  {number} seed You can optionally supply a seed (unsigned integer, 32-bit max) for alternate streams of the same input
 * @returns a 53 bits hash number
 */
export function hash(str: string, seed = 0): number {
    let h1 = 0xdeadbeef ^ seed,
        h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }

    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Remove chars from start and end of a string value by default
 * Use start or end options to trim one of both side
 * @param value String value 
 * @param chars Characters to trim from the string value
 * @param options
 * @param options.start Will do the trim only at the start of the string value
 * @param options.end Will do the trim only at the end of the string value
 * @example
 * trimChars("000123456000", "0") => "123456"
 * trimChars("000123456000", "0", { start: true }) => "123456000"
 * trimChars("LoLoremIpsumLo", "Lo") => "remIpsum"
 * trimChars("LoremIpsumLo", "Lo", { end: true }) => "LoremIpsum"
 */
export function trimChars(value: string, chars: string, options?: { start?: boolean, end?: boolean }): string {
    // Don't process if nothing to trim
    if (!chars) { return value; }

    // Trim start and end by default, else look at each start/end options
    if (!options || (options.start && options.end)) {
        const regex = new RegExp(`^(${chars})+|(${chars})+$`, "g");
        value = value.replace(regex, "");
    } else {
        if (options?.start) {
            const regex = new RegExp(`^(${chars})+`);
            value = value.replace(regex, "");
        }
        if (options?.end) {
            const regex = new RegExp(`(${chars})+$`);
            value = value.replace(regex, "");
        }
    }

    return value;
}